import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { generateRandomString } from "utils/helper";
import { PAGE_TYPE } from "../schemas/incomeDeductionSchema";
import * as services from "../service/incomeDeductionApi";
import { setIncomeDeductionList, setIsLoading } from "../slices/payrollSlice";
import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";

function toParams(params = {}) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    ...params,
    page: pagination.page || 0,
    size: pagination.pageSize || 10,
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

function useIncomeDeductionList(props = {}) {
  const { t, i18n } = useTranslation();
  const { pageType = "" } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.payroll.isLoading);
  const incomeDeductionList = useSelector(
    (state) => state.payroll.incomeDeductionList
  );
  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorter, setSorter] = useState([]);

  const link = useMemo(() => {
    if (pageType === PAGE_TYPE.INCOME) {
      return "income";
    }
    return "deduction";
  }, [pageType]);

  const getIncomeDeductionList = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getIncomeDeductionList({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
          type: pageType,
        });
        dispatch(
          setIncomeDeductionList(
            response.data?.stpIncomeDeductionPagingResp || []
          )
        );
        const modifiedPagination = {
          ...pagination,
          range: [
            (pagination.page - 1) * pagination.size + 1,
            Math.min(
              pagination.page * pagination.size,
              response.data?.totalItems
            ),
          ],
          total: response.data?.totalItems,
        };
        setPagination(modifiedPagination);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pageType, pagination, dispatch]
  );

  const onSubmit = useCallback(
    async (values) => {
      const modifiedValues = { ...values, companyId: currentCompany };
      try {
        dispatch(setIsLoading(true));
        const response = await services.getIncomeDeductionList({
          ...values,
          requestId: generateRandomString(),
          companyId: currentCompany,
          type: pageType,
          page: pagination.page - 1,
          size: pagination.size,
        });
        dispatch(
          setIncomeDeductionList(
            response.data?.stpIncomeDeductionPagingResp || []
          )
        );
        const modifiedPagination = {
          ...pagination,
          range: [
            (pagination.page - 1) * pagination.size + 1,
            Math.min(
              pagination.page * pagination.size,
              response.data?.totalItems
            ),
          ],
          total: response.data?.totalItems,
        };
        setPagination(modifiedPagination);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pageType, pagination, dispatch]
  );

  const onCreate = useCallback(() => {
    navigate(`/configuration/payroll/${link}/create`);
  }, [link, navigate]);

  const onEdit = useCallback(
    (record) => {
      if (record.incomeDeductionId) {
        navigate(`/configuration/payroll/${link}/${record.incomeDeductionId}/edit`);
      }
    },
    [link, navigate]
  );

  const onCancel = useCallback(() => {
    navigate(`/configuration/payroll/${link}`);
  }, [link, navigate]);

  const onDelete = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          incomeDeductionId: record.incomeDeductionId,
          requestId: generateRandomString(),
          companyId: currentCompany,
          statusFlag: "DELETE",
        };
        await services.manageStatusIncomeDeduction(values);
        message.success(t("common.success"))
        const modifiedParams = toParams({
          page: pagination.current - 1,
          pageSize: pagination.pageSize,
          sortBy: sorter,
        });
        getIncomeDeductionList(modifiedParams);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, getIncomeDeductionList]
  );

  const handleOnChange = useCallback(
    (tablePagination, _, tableSorter) => {
      setPagination(tablePagination);
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      setSorter(modifiedSorter);
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },
        sortBy: modifiedSorter,
      });
      // TODO: If sorting or filtering has changed, Change page to 0
      getIncomeDeductionList(modifiedParams);
    },
    [getIncomeDeductionList]
  );

  const handleOnStatus = useCallback(
    async (val, record) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to change status ?</span>
          : <span>คุณยืนยันการเปลี่ยนสถานะใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const values = {
              incomeDeductionId: record.incomeDeductionId,
              requestId: generateRandomString(),
              companyId: currentCompany,
              statusFlag: val ? "ACTIVE" : "INACTIVE",
            };
            await services.manageStatusIncomeDeduction(values);
            // alert({ message: "Success" });
            const modifiedIncomeDeductionList = incomeDeductionList.map((i) => {
              if (record.incomeDeductionId === i.incomeDeductionId) {
                return { ...i, statusFlag: val ? "ACTIVE" : "INACTIVE" };
              }
              return i;
            });
            dispatch(setIncomeDeductionList(modifiedIncomeDeductionList));
            message.success(t("common.success"))
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    },
    [currentCompany, incomeDeductionList, dispatch]
  );

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getIncomeDeductionList({
        page: 0,
        size: 10,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany]);

  return {
    isLoading,
    pagination,
    incomeDeductionList,
    onCreate,
    onCancel,
    onSubmit,
    onEdit,
    onDelete,
    onChange: handleOnChange,
    onStatus: handleOnStatus,
  };
}

export default useIncomeDeductionList;
